/**
 * Move-to-position picker — TASK 9.1 (mock-defect fix 5).
 *
 * The brief (Flow 5/7, Req 15) requires moving a queue item to an ARBITRARY
 * position N, but the mock offers only top/bottom/±1/drag (ui-contract §10.3
 * "⚠ Mock gap — move-to-position-N"). This overlay is that missing picker:
 * a single-field numeric form, patterned on ../../app/overlays.js#InputModal
 * so it composes with OverlayStack (focus trap, Escape, back-dismiss) and is
 * fully keyboard operable — which matters because move-to-N is ALSO the
 * screen-reader path AC 7.2 requires (a reorder must never demand a drag).
 *
 * ── THE 1-BASED / 0-BASED SEAM, precisely ─────────────────────────────────
 * A human says "move it to 3", counting the UP-NEXT list from 1. The API's
 * `{kind:'to', position}` (dto.ts `moveDirectiveSchema`) is a 0-BASED index
 * into the WHOLE queue — a space that also contains the currently-playing
 * item (and any already-played prefix `setCurrentIndex` left behind), none of
 * which the user can see or count. Both conversions live in ONE exported pure
 * function, {@link moveToPositionDirective}, so the off-by-one can be pinned
 * by a unit test instead of hiding inside a click handler: display slot 1 is
 * absolute position `firstUpcomingPosition`, display slot N is
 * `firstUpcomingPosition + (N - 1)`.
 *
 * Validation is INLINE (ui-contract §10.5: `<p role="alert">`, announced,
 * below the field) and a bad value never closes the dialog. A position past
 * the end would be CLAMPED server-side rather than rejected (dto.ts), but the
 * picker still refuses it locally — "moved to 999" silently becoming "moved
 * to 12" is a lie the UI can prevent for free.
 *
 * @module
 */

import { controlTestId } from '../../app/testids.js';
import { append, el } from '../../components/primitives/dom.js';

/** Inline error for a value that is not a whole number. */
export const POSITION_NOT_A_NUMBER_ERROR = 'Enter a whole number.';

/**
 * Inline error for a whole number outside the list.
 *
 * @param {number} total
 * @returns {string}
 */
export function positionRangeError(total) {
  return `Enter a position between 1 and ${total}.`;
}

/**
 * Converts the USER's 1-based up-next position into the API's 0-based
 * absolute `{kind:'to'}` directive.
 *
 * @param {number} displayPosition 1-based position in the up-next list as the
 *   user sees it ("move to 3")
 * @param {number} firstUpcomingPosition the ABSOLUTE 0-based queue position of
 *   up-next slot 1 (the queue view passes `playingIndex + 1` — the API's
 *   position space includes the currently-playing item, see the header)
 * @returns {{ kind: 'to', position: number }}
 */
export function moveToPositionDirective(displayPosition, firstUpcomingPosition) {
  return { kind: 'to', position: firstUpcomingPosition + (Math.trunc(displayPosition) - 1) };
}

/**
 * Builds the picker overlay.
 *
 * @param {{
 *   title: string,
 *   position: number,
 *   total: number,
 *   onSubmit: (displayPosition: number) => string|null,
 *   onCancel: () => void,
 * }} options
 *   `title` is the track title (it names the dialog — OverlayStack#open
 *   THROWS on a dialog without an accessible name). `position`/`total` are
 *   1-based display values: the row's current up-next slot and the up-next
 *   count. `onSubmit` receives the VALIDATED 1-based position and follows the
 *   InputModal contract — return an error string to display inline, or null
 *   on success (the caller closes the overlay itself, usually via
 *   `overlays.closeTop({then})`).
 * @returns {import('../../app/OverlayStack.js').OverlayDescriptor}
 */
export function MoveToPositionSheet(options) {
  const { title, position, total, onSubmit, onCancel } = options;

  const scrim = el('div', {
    className: 'sm-modal-scrim',
    attrs: { role: 'dialog', 'aria-modal': 'true', 'aria-label': `Move “${title}” to position` },
  });

  const input = el('input', {
    className: 'sm-input sm-input--block',
    attrs: {
      id: 'sm-mtp',
      type: 'number',
      inputmode: 'numeric',
      min: '1',
      max: String(total),
      step: '1',
      'aria-describedby': 'sm-mtp-hint sm-mtp-error',
    },
  });
  // Prefill with the CURRENT slot so a keyboard user can arrow up/down from
  // where the item already is instead of typing from scratch.
  /** @type {HTMLInputElement} */ (input).value = String(position);

  // Announced on failure (ui-contract §10.5 error display).
  const error = el('p', {
    className: 'sm-form-error sm-form-error--modal',
    attrs: { id: 'sm-mtp-error', role: 'alert', hidden: true },
  });

  /** @param {string} message */
  function fail(message) {
    error.textContent = message;
    error.removeAttribute('hidden');
    input.focus({ preventScroll: true });
  }

  const form = el('form', {
    className: 'sm-modal sm-modal--input sm-anim-up-fast',
    on: {
      submit: (event) => {
        event.preventDefault();
        const raw = /** @type {HTMLInputElement} */ (input).value.trim();
        // `type=number` blocks most garbage but not `1.5`, `1e3` or an empty
        // string — the integer gate here is the one that counts.
        if (!/^-?\d+$/.test(raw)) {
          fail(POSITION_NOT_A_NUMBER_ERROR);
          return;
        }
        const target = Number(raw);
        if (target < 1 || target > total) {
          fail(positionRangeError(total));
          return;
        }
        const message = onSubmit(target);
        if (message === null) return;
        fail(message);
      },
    },
  });

  append(
    form,
    el('div', { className: 'sm-modal__title sm-modal__title--teal', text: 'Move to position' }),
    el('label', {
      className: 'sm-form-label',
      text: `Position (1–${total})`,
      attrs: { for: 'sm-mtp' },
    }),
    input,
    el('p', {
      className: 'sm-hint',
      text: `Currently at position ${position} of ${total}.`,
      attrs: { id: 'sm-mtp-hint' },
    }),
    error,
    append(
      el('div', { className: 'sm-actions sm-actions--end sm-actions--end-top' }),
      el('button', {
        className: 'sm-btn sm-btn--ghost',
        text: 'Cancel',
        testId: controlTestId('Cancel'),
        attrs: { type: 'button' },
        on: { click: () => onCancel() },
      }),
      el('button', {
        className: 'sm-btn sm-btn--modal-confirm',
        text: 'Move',
        testId: controlTestId('Move'),
        attrs: { type: 'submit' },
        styleProps: { background: 'var(--sm-color-teal)' },
      }),
    ),
  );
  scrim.append(form);

  return { kind: 'input', element: scrim, dialog: scrim, initialFocus: input };
}
